/**
 * Wizard step definitions. Single source of truth for the stepper labels,
 * route paths, and resume logic. Signup + verify email live in the auth flow
 * and are not part of this rail.
 *
 * The wizard is THREE steps. Finishing step 3 completes onboarding and hands
 * off to the preparing screen, then the dashboard.
 *
 * Verify and Test payment used to be steps 4 and 5. They are no longer part of
 * the wizard, but their routes still exist and still work: verification gates
 * going live (see ThemeContext canGoLive), and the dashboard's empty state links
 * straight to /onboarding/payment. They are reached from the dashboard now
 * rather than being required before a merchant can get in.
 */

export const STEPS = [
  { key: 'business', path: '/onboarding/business', label: 'Business' },
  { key: 'use-cases', path: '/onboarding/use-cases', label: 'What you need' },
  { key: 'plan', path: '/onboarding/plan', label: 'Plan' },
];

/** Handoff screen shown after the last step, and on Save & exit. */
export const PREPARING_PATH = '/onboarding/preparing';

// onboarding_status -> the step the merchant should resume at. A status that is
// absent from this map means the wizard is finished and the caller should leave
// onboarding: that now covers `completed` AND the statuses that used to sit
// after step 3 (`plan_done`, `activation_done`, `payment_done`), so a merchant
// stored mid-old-flow is let straight through instead of being sent back to a
// step that no longer exists.
const STATUS_TO_STEP = {
  started: 'business',
  business_done: 'use-cases',
  usecases_done: 'plan',
};

/**
 * Route path of the first incomplete step for a given onboarding state.
 * Returns null when the merchant should not be in the wizard at all.
 *
 * @param {{ onboarding_status?: string, onboarding_dismissed?: boolean, onboarding_applicable?: boolean }} state
 * @returns {string|null}
 */
export function firstIncompleteStepPath(state) {
  // Super admins have no merchant account to set up. The server reports this
  // as `applicable: false`; the route guard also blocks them, so this is the
  // second of two independent checks rather than the only one.
  if (state?.onboarding_applicable === false) return null;

  // Leaving via "Save & exit" is a decision, not a pause. Checked before the
  // status, so a merchant who exits on step 1 is not sent back to step 1 on
  // their next login -- which is the whole point of the button.
  if (state?.onboarding_dismissed) return null;

  const status = state?.onboarding_status;
  if (!status) return STEPS[0].path;
  const key = STATUS_TO_STEP[status];
  // Not in the map: the wizard is done (completed, or a status from the old
  // six-step flow). Null tells the caller to leave onboarding.
  if (!key) return null;
  return STEPS.find((s) => s.key === key)?.path || STEPS[0].path;
}

/** Zero-based index of a step key, for the stepper progress display. */
export function stepIndex(key) {
  return STEPS.findIndex((s) => s.key === key);
}
